"use client";
import React, { useState } from 'react';

/**
 * FIX-49: deterministic, dependency-free identicon for wallets without a
 * real avatar image.
 *
 * The fix plan's snippet suggested `npm install @dicebear/core
 * @dicebear/collection`, but this sandbox's npm registry access returns
 * 403 Forbidden for scoped packages (confirmed via `npm view @dicebear/core`),
 * mirroring the same registry-block encountered for `next-pwa` during FIX-45.
 * So instead of a new dependency, this hashes the wallet address into a
 * small left-right-symmetric grid + a hue (classic "identicon" look) and
 * renders it as inline SVG - same address always produces the same
 * pattern/color, no randomness, no network call, no new dependency.
 */

// Simple deterministic 32-bit hash (FNV-1a). Not cryptographic - it only
// needs to spread small address differences across the output bits.
function hashString(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

// xorshift32 PRNG seeded from the address hash, so the grid + hue can be
// derived deterministically from a single seed without re-hashing substrings.
function makeRng(seed: number) {
  let x = seed || 0x9e3779b9;
  return function next(): number {
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    x = x >>> 0;
    return x;
  };
}

const GRID_SIZE = 5;
// Only columns 0..HALF_COLS-1 are randomized; the remaining columns mirror
// them for left-right symmetry, which is what makes the output read as a
// deliberate "identicon" mark rather than plain noise.
const HALF_COLS = Math.ceil(GRID_SIZE / 2);

export type WalletIdenticonProps = {
  address?: string | null;
  size?: number;
  className?: string;
};

export function WalletIdenticon({ address, size = 40, className = '' }: WalletIdenticonProps) {
  const normalized = String(address || '').trim().toLowerCase() || 'unknown-wallet';
  const next = makeRng(hashString(normalized));

  const hue = next() % 360;
  const bgHue = (hue + 150) % 360;
  const fg = `hsl(${hue}, 65%, 55%)`;
  const bg = `hsl(${bgHue}, 35%, 16%)`;

  const rows: boolean[][] = [];
  for (let row = 0; row < GRID_SIZE; row++) {
    const half: boolean[] = [];
    for (let col = 0; col < HALF_COLS; col++) {
      half.push((next() & 1) === 1);
    }
    const full = half.slice();
    for (let col = HALF_COLS; col < GRID_SIZE; col++) {
      full.push(half[GRID_SIZE - 1 - col]);
    }
    rows.push(full);
  }

  return (
    <svg
      viewBox={`0 0 ${GRID_SIZE} ${GRID_SIZE}`}
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label="Wallet identicon"
    >
      <rect x={0} y={0} width={GRID_SIZE} height={GRID_SIZE} fill={bg} />
      {rows.flatMap((cells, row) =>
        cells.map((on, col) =>
          on ? <rect key={`${row}-${col}`} x={col} y={row} width={1} height={1} fill={fg} /> : null
        )
      )}
    </svg>
  );
}

export type WalletAvatarProps = {
  address?: string | null;
  avatarUrl?: string | null;
  size?: number;
  className?: string;
  alt?: string;
};

/**
 * Drop-in replacement for "<img src={avatarUrl || somePlaceholder}>": renders
 * the real avatar when one is set and loads successfully, otherwise renders
 * a WalletIdenticon keyed on the wallet address - so a wallet without an
 * uploaded/linked avatar gets a stable, recognizable visual identity instead
 * of a generic icon, and a broken/expired avatar URL degrades to the same
 * identicon instead of a missing-image icon.
 */
export function WalletAvatar({ address, avatarUrl, size = 40, className = '', alt = 'Wallet avatar' }: WalletAvatarProps) {
  const [failed, setFailed] = useState(false);
  const hasImage = Boolean(avatarUrl) && !failed;

  if (!hasImage) {
    return <WalletIdenticon address={address} size={size} className={className} />;
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img src={avatarUrl as string} alt={alt} className={className} onError={() => setFailed(true)} />
  );
}

export default WalletIdenticon;
